import {
  FC,
  Dispatch,
  SetStateAction,
  ReactElement,
  Children,
  useMemo,
  useRef,
} from 'react';
import {
  AdaptedEntity,
  Column,
  GenericEntityType,
  Pagination,
  TableCellProps,
  TemplateEntity,
  TextFieldValue,
  WbTable,
} from '@agilelab/plugin-wb-platform';
import {
  Box,
  Checkbox,
  createStyles,
  FormControl,
  makeStyles,
  Radio,
  Typography,
} from '@material-ui/core';
import { Fragment, cloneElement, isValidElement } from 'react';
import { isEqual } from 'lodash';
import { getLabelAndValue } from './utils';
import { parseNunjucks } from '@agilelab/plugin-wb-platform-common';
import { AiToggle } from './MultiSelection/AiToggle';
import { useGeneralPurposePickerContext } from '../GeneralPurposePickerContext';

const renderRow = (
  row: GenericEntityType,
  path: string,
  classes: ReturnType<typeof useStyles>,
) => {
  const parsedNunjuck = parseNunjucks(path, row);
  if (path === 'aiBestMatchLabel') {
    return (
      <Typography>
        <span className={classes.boxBest}>{parsedNunjuck}</span>
      </Typography>
    );
  }
  return <Typography>{parsedNunjuck}</Typography>;
};

interface SelectableEntityTableProps {
  loading: boolean;
  data: GenericEntityType[];
  entities: TextFieldValue[];
  adapter: (entity: GenericEntityType) => AdaptedEntity;
  onChangeHandler: (entities: TextFieldValue[]) => void;
  filtersSection: () => JSX.Element;
  count: number;
  pagination: Pagination;
  columns: Column[];
  setPagination: Dispatch<SetStateAction<Pagination>>;
  multiple?: boolean;
  prevCursor?: string;
  nextCursor?: string;
  templateEntity: TemplateEntity;
  isAiMatchAvailable?: boolean;
  getAllFilteredEntities?: () => Promise<GenericEntityType[]>;
}

const useStyles = makeStyles(
  theme =>
    createStyles({
      formControl: {
        width: '100%',
        overflow: 'auto',
        height: '100%',
        justifyContent: 'space-between',
      },
      boxBest: {
        padding: `${theme.spacing(0.5)}px ${theme.spacing(1)}px`,
        backgroundColor: theme.palette.white,
        border: 'solid 1px transparent',
        borderRadius: '4px',
        background:
          'linear-gradient( #ffffff, #ffffff) padding-box,linear-gradient(90deg, #EE72D3, #7C4DFF) border-box',
        whiteSpace: 'nowrap',
      },
      aiSuggestedRow: {
        backgroundColor: `${theme.palette.bkg.accent} !important`,
        '& td': {
          borderBottom: `1px solid ${theme.palette.accent.light}`,
        },
      },
      aiFilterButtonClass: {
        width: 'fit-content',
      },
    }),
  { name: 'selectionTable' },
);

export const SelectableEntityTable: FC<SelectableEntityTableProps> = ({
  loading,
  data,
  entities,
  adapter,
  onChangeHandler,
  filtersSection,
  multiple = false,
  pagination,
  setPagination,
  count,
  prevCursor,
  nextCursor,
  columns,
  templateEntity,
  isAiMatchAvailable = false,
  getAllFilteredEntities,
}) => {
  const { isAiGenerated, fetchAiBestMatch, setAiState, setSuggestedItems } =
    useGeneralPurposePickerContext();
  const classes = useStyles();
  // Reference to keep track of AI added entities
  const aiAddedRef = useRef<TextFieldValue[]>(entities);

  const handleChange = async (checked?: boolean) => {
    setAiState(checked ?? false);
    setSuggestedItems([]);

    if (checked) {
      // fetch all available entities not paginated
      const options = (await getAllFilteredEntities?.()) ?? [];
      const result = await fetchAiBestMatch(options);
      const aiBestMatch =
        result?.map(row =>
          getLabelAndValue(row, adapter(row), templateEntity, true),
        ) ?? [];

      // Add AI best matches that are not already in the selected entities
      const toAdd = aiBestMatch.filter(
        ai => !entities.some(e => isEqual(e.value, ai.value)),
      );
      aiAddedRef.current = toAdd;
      onChangeHandler([...entities, ...toAdd]);
    } else {
      const newSelection = (entities ?? []).filter(
        e => !aiAddedRef.current.some(ai => isEqual(e, ai)),
      );
      aiAddedRef.current = [];
      onChangeHandler(newSelection);
    }
  };

  const handleChangePage = (newPage: number) => {
    setPagination(v => ({
      limit: v.limit ?? 0,
      currentPage: newPage,
      cursor: pagination.currentPage > newPage ? prevCursor : nextCursor,
    }));
  };

  const handleChangeRowsPerPage = (limit: number) => {
    setPagination({
      limit: limit,
      currentPage: 0,
      cursor: undefined,
    });
  };

  const Selector = ({ row }: { row: GenericEntityType }) => {
    const adaptedRow = getLabelAndValue(row, adapter(row), templateEntity);

    if (templateEntity.displayName) {
      adaptedRow.kindDisplayName = templateEntity.displayName;
    }

    return multiple ? (
      <>
        <Checkbox
          onChange={(event, checked) => {
            event.stopPropagation();

            if (checked) {
              onChangeHandler([...entities, adaptedRow]);
            } else {
              onChangeHandler([
                ...entities?.filter(e => !isEqual(e.value, adaptedRow.value)),
              ]);
            }
          }}
          checked={
            !!entities?.find((e: TextFieldValue) => {
              return isEqual(e.value, adaptedRow.value);
            })
          }
          color="primary"
        />
      </>
    ) : (
      <Radio
        onClick={e => {
          e.stopPropagation();
          onChangeHandler([adaptedRow]);
        }}
        checked={isEqual(entities[0], adaptedRow)}
        color="primary"
      />
    );
  };

  const handleColumns = (): TableCellProps<GenericEntityType>[] => {
    const modifiedColumns = columns.map((column, idx) => ({
      field: column.name,
      fieldRender: (row: GenericEntityType) => {
        if (idx === 0 && isAiGenerated(row)) {
          return (
            <Box
              key={column.name}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography>{parseNunjucks(column.path, row)}</Typography>

              <Typography className={classes.boxBest} variant="caption">
                Best match
              </Typography>
            </Box>
          );
        }
        return renderRow(row, column.path, classes);
      },
      headerName: column.name,
    }));

    return [
      {
        field: 'selector',
        fieldRender: (row: GenericEntityType) => <Selector row={row} />,
        headerName: '',
        cellProps: {
          width: '5%',
        },
      },
      ...modifiedColumns,
    ];
  };

  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      const aBest = isAiGenerated(a);
      const bBest = isAiGenerated(b);
      if (aBest === bBest) return 0;
      return aBest ? -1 : 1;
    });
  }, [data, isAiGenerated]);

  const aiToggleComponent = (
    <AiToggle
      handleChange={handleChange}
      aiFilterButtonClass={classes.aiFilterButtonClass}
    />
  );

  const filtersSectionWithAI = () => {
    const section = filtersSection();
    if (isValidElement(section) && (section as ReactElement).props.children) {
      return cloneElement(section, {}, [
        isAiMatchAvailable && aiToggleComponent,
        ...Children.toArray((section as ReactElement).props.children),
      ]);
    }
    return (
      <Fragment>
        {isAiMatchAvailable && aiToggleComponent}
        {section}
      </Fragment>
    );
  };

  return (
    <>
      {filtersSectionWithAI()}
      <FormControl className={classes.formControl}>
        <WbTable
          styles={{
            container: {
              flex: 1,
            },
          }}
          components={{
            tableLoader: { loading },
            tableContent: {
              columns: handleColumns(),
              rows: sortedData,
              rowDynamicClassName: row => {
                return isAiGenerated(row) ? classes.aiSuggestedRow : undefined;
              },
            },
          }}
          pagination={{
            countlessOptions: pagination.countlessOptions,
            rowsPerPageOptions: [5, 10, 25],
            count: count,
            limit: pagination.limit ?? 0,
            currentPage: pagination.currentPage,
            onPageChange: handleChangePage,
            onRowsPerPageChange: handleChangeRowsPerPage,
          }}
        />
      </FormControl>
    </>
  );
};
